import { yupResolver } from '@hookform/resolvers/yup';
import { useLocalSearchParams, useRouter } from 'expo-router';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useClient, useMutation, useQuery } from 'urql';
import { v4 } from 'uuid';
import {
  BadgeEditFormFragment,
  type BadgeEditFormValues,
  badgeEditValidator,
} from '@/components/Badge/BadgeEdit/BadgeEdit.types';
import ButtonToast, { useButtonToast } from '@/components/Custom/ButtonToast';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardAwareScrollView from '@/components/Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import TitleColorFields from '@/components/FormFields/TitleColorFields';
import Box from '@/components/UI/Box';
import Button from '@/components/UI/Button';
import FormContentBoundary from '@/components/UI/FormContentBoundary';
import { graphql, readFragment } from '@/gql/graphql';
import useConfig from '@/hooks/useConfig';
import getRandomColor from '@/utils/getRandomColor';
import { getCsvValues } from './components/BadgeEdit.utils';
import BadgeEditAttribute from './components/BadgeEditAttribute';
import BadgeEditAttributePreview from './components/BadgeEditAttributePreview';
import BadgeEditHeader from './components/BadgeEditHeader';

const GetBadgeEdit = graphql(/* GraphQL */ `
  query GetBadgeEdit($badgeId: uuid!) {
    badge_by_pk(id: $badgeId) {
      id
      is_official
      permissions
    }
  }
`);

const GetBadgeEditForm = graphql(/* GraphQL */ `
  query GetBadgeEditForm($badgeId: uuid!) {
    badge_by_pk(id: $badgeId) {
      ...BadgeEditForm
    }
  }
`, [BadgeEditFormFragment]);

const UpsertBadge = graphql(/* GraphQL */ `
  mutation UpsertBadge(
    $id: uuid!
    $rootEventId: uuid
    $teamId: uuid
    $title: String!
    $color: String!
    $attributePreviewId: uuid
    $attributes: [badge_attribute_insert_input!]!
    $deletedAttributeIds: [uuid!]!
  ) {
    delete_badge_attribute(where: { id: { _in: $deletedAttributeIds } }) {
      affected_rows
    }
    initialInsert: insert_badge_one(
      on_conflict: { constraint: badge_pkey, update_columns: [title, color] }
      object: {
        id: $id
        root_event_id: $rootEventId
        team_id: $teamId
        title: $title
        color: $color
      }
    ) {
      id
      title
      color
    }
    insert_badge_attribute(
      on_conflict: {
        constraint: badge_attribute_pkey
        update_columns: [title, attribute_type, is_required, options, order]
      }
      objects: $attributes
    ) {
      affected_rows
    }
    attributePreviewUpdate: update_badge_by_pk(
      _set: { attribute_preview_id: $attributePreviewId }
      pk_columns: { id: $id }
    ) {
      id
    }
  }
`);

export default function BadgeEdit() {
  const client = useClient();
  const { back } = useRouter();
  const { badge_attribute_max } = useConfig();
  const maxAttributes = Number.parseInt(badge_attribute_max || '', 10);

  const { eventId, badgeId, teamId } = useLocalSearchParams<{
    eventId?: string;
    badgeId?: string;
    teamId?: string;
  }>();

  const [{ data }] = useQuery({
    query: GetBadgeEdit,
    variables: {
      badgeId: badgeId!,
    },
    pause: !badgeId,
  });

  const badge = data?.badge_by_pk;

  const form = useForm<BadgeEditFormValues>({
    mode: 'onChange',
    resolver: yupResolver(badgeEditValidator),
    defaultValues: async () => {
      if (!badgeId) {
        return {
          title: '',
          color: getRandomColor(),
          badge_attributes: [],
        };
      }

      const { data: formData } = await client.query(
        GetBadgeEditForm,
        {
          badgeId: badgeId!,
        },
        { requestPolicy: 'network-only' },
      );

      const formBadge = readFragment(
        BadgeEditFormFragment,
        formData?.badge_by_pk,
      );

      return {
        id: formBadge?.id,
        title: formBadge?.title || '',
        color: formBadge?.color || '',
        attribute_preview_id: formBadge?.attribute_preview_id,
        badge_attributes: formBadge?.badge_attributes || [],
      };
    },
  });

  const {
    formState: { isLoading, defaultValues },
    control,
    handleSubmit,
    setValue,
  } = form;
  const title = useWatch({ control, name: 'title' });
  const color = useWatch({ control, name: 'color' });

  const attributes = useFieldArray({
    control,
    name: 'badge_attributes',
  });

  const [toast, setToast] = useButtonToast();
  const [, upsertBadge] = useMutation(UpsertBadge);

  const onPressSave = async (values: BadgeEditFormValues) => {
    const upsertedBadgeId = badgeId || v4();

    const defaultIds =
      defaultValues?.badge_attributes?.flatMap((badgeAttribute) =>
        badgeAttribute?.id ? [badgeAttribute.id] : [],
      ) || [];

    const newIds =
      values.badge_attributes?.map((badgeAttribute) => badgeAttribute?.id) ||
      [];

    const deletedAttributeIds = defaultIds.filter(
      (defaultId) => defaultId && !newIds.includes(defaultId),
    );

    const { error } = await upsertBadge({
      id: upsertedBadgeId,
      title: values.title,
      color: values.color,
      rootEventId: eventId!,
      teamId: teamId!,
      attributePreviewId: values.badge_attributes?.length
        ? values.attribute_preview_id || undefined
        : undefined,
      attributes:
        values.badge_attributes?.map((badgeAttribute, index) => ({
          ...badgeAttribute,
          badge_id: upsertedBadgeId,
          options_csv: undefined,
          order: index,
          options: badgeAttribute.options_csv
            ? getCsvValues(badgeAttribute.options_csv)
            : [],
        })) || [],
      deletedAttributeIds,
    });

    if (!error) {
      back();
      return;
    }

    setToast({
      text: 'Something Went Wrong',
      variant: 'error',
      icon: 'close',
    });
  };

  const canDelete = !badge?.is_official && badge?.permissions?.delete_badge;

  return (
    <FormProvider {...form}>
      <BadgeEditHeader canDelete={canDelete} />
      <FormContentBoundary loading={isLoading}>
        <ModalKeyboardAwareScrollView>
          <Box direction="vertical" space="sm" p="md">
            <TitleColorFields
              autoFocus={!badgeId}
              color={color}
              placeholder="Badge title"
              title={title}
              onChangeColor={(nextColor) => {
                setValue('color', nextColor, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              onChangeTitle={(nextTitle) => {
                setValue('title', nextTitle, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
            />
            {attributes.fields.length ? <BadgeEditAttributePreview /> : null}
            {attributes?.fields?.map((attribute, index) => {
              return (
                <BadgeEditAttribute
                  remove={attributes.remove}
                  swap={attributes.swap}
                  key={attribute.id}
                  index={index}
                  attributeCount={attributes.fields.length}
                />
              );
            })}
            <Button
              testID="BadgeEditAddAttributeButton"
              disabled={attributes.fields.length >= maxAttributes}
              onPress={() => {
                attributes.append({
                  id: v4(),
                  title: '',
                  options_csv: '',
                  attribute_type: 'text',
                  is_required: false,
                });
              }}
              appearance="outline"
              variant="positive"
            >
              <Button.Text>Add Attribute</Button.Text>
            </Button>
          </Box>
        </ModalKeyboardAwareScrollView>
        <ButtonToast {...toast} />
        <ModalKeyboardStickyView>
          <FormSaveButton
            info={{ text: 'Save' }}
            onPress={() => handleSubmit(onPressSave)()}
          />
        </ModalKeyboardStickyView>
      </FormContentBoundary>
    </FormProvider>
  );
}
